import { useQuery } from '@tanstack/react-query'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { ContactCompany } from 'models/companies'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import useApiService from 'services/api'
import { useDebounce } from 'use-debounce'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { ContactCreateRequest } from '../../models/contacts'

type CompanyOption = {
  label: string | React.JSX.Element
  value: ContactCreateRequest['contact_company'] | ContactCompany
}

export function CompanyAutocomplete<T extends FieldValues>({
  control,
  name,
  initialCompany,
  placeholder,
  initialSearch,
  menuPosition,
  label = 'Company',
  onFetchDone,
  disabled,
  disableCreate = false,
  readOnly,
  autoSelectFirstOption = false,
  className,
  height = '50px',
  chainsOnly = false,
  subcategoryId,
  noOptionsMessage,
}: {
  control: Control<T, any>
  name: Path<T>
  initialCompany?: ContactCompany
  placeholder?: string
  initialSearch?: string
  menuPosition?: 'fixed' | 'absolute'
  label?: string | null
  onFetchDone?: () => void
  disabled?: boolean
  disableCreate?: boolean
  readOnly?: boolean
  autoSelectFirstOption?: boolean
  className?: string
  height?: string
  chainsOnly?: boolean
  subcategoryId?: number
  /** Override the dropdown's "no options" message. Forwarded to
   *  CreatableSelect → react-select. Receives the current input value
   *  so callers can differentiate "type to search" from "no matches". */
  noOptionsMessage?: (obj: { inputValue: string }) => React.ReactNode
}) {
  const [searchCompany, setSearchCompany] = useState(initialSearch || '')
  const [search] = useDebounce(searchCompany, 500)
  const selectInstanceRef = useRef<any>(null)

  const api = useApiService()
  const {
    enableK12Table,
    enableCnUTable,
    enableHospitals,
    enableNursingHomes,
    enableLodging,
  } = useFeatureFlagService()

  const {
    field: { onChange },
  } = useController({ name: name, control })

  const { data, isFetching, isFetched } = useQuery({
    queryKey: ['companies_list_search', search, subcategoryId],
    queryFn: async ({ signal }) => {
      const params: any = { search: search }
      if (subcategoryId) {
        params.subcategory_id = subcategoryId
      }
      return await api.searchCompaniesList(params, signal)
    },
    enabled: search !== '' && !disabled,
  })

  useEffect(() => {
    if (onFetchDone && isFetched) onFetchDone()
  }, [isFetched])

  const companiesOptions = useMemo((): CompanyOption[] => {
    const getInitialCompanyLabel = (name: string, domain?: string) => {
      if (domain) {
        return (
          <>
            <span>
              <b>{name}</b>
              {domain ? ` - ${domain}` : ''}
            </span>
          </>
        )
      }
      return <b>{name}</b>
    }

    // note: initial contact company does not have sub category name, just id
    // note: the control will not display the initial value in the ui component
    //       unless we provide the full initialCompany (ContactCompany) object
    if (initialCompany && !searchCompany) {
      return [
        {
          label: getInitialCompanyLabel(
            initialCompany.name!,
            initialCompany.domain
          ),
          value: {
            ...initialCompany,
          } as ContactCompany,
        },
      ]
    }

    const out =
      data
        ?.filter((c) => {
          if (chainsOnly && c.sub_category_slug !== 'restaurants-bars') {
            return false
          }

          if (!enableK12Table && c.sub_category_slug === 'education-k-12') {
            return false
          }

          if (!enableCnUTable && c.sub_category_slug === 'education-cu') {
            return false
          }

          if (
            !enableHospitals &&
            c.sub_category_slug === 'healthcare-hospitals'
          ) {
            return false
          }
          if (
            !enableNursingHomes &&
            c.sub_category_slug === 'healthcare-nursinghomes'
          ) {
            return false
          }
          if (!enableLodging && c.sub_category_slug === 'lodging-hospitality') {
            return false
          }

          return true
        })
        .map((c) => {
          return {
            label: (
              <div className={'flex flex-col'}>
                <span>
                  <b>{c.name}</b>
                  {c.domain ? ` - ${c.domain}` : ''}
                </span>
                <small>{c.sub_category_name}</small>
              </div>
            ),
            value: {
              id: c.contact_company_id,
              domain: c.domain,
              name: c.name,
              category: c.category,
              sub_category: c.sub_category,
            },
          }
        }) || []

    return out
  }, [data, initialCompany])

  useEffect(() => {
    if (autoSelectFirstOption && companiesOptions.length > 0) {
      const firstOption = companiesOptions[0]
      onChange(firstOption.value)
    }
  }, [companiesOptions, autoSelectFirstOption])

  // Block Enter selection in two cases:
  //  1. While the company search is in flight (debounce or fetch active)
  //     — pressing Enter against stale options or a not-yet-rendered
  //     create option would otherwise pick whatever happened to be
  //     focused in the menu, often the wrong company.
  //  2. When the focused option is the creatable "Create new" entry —
  //     creating a brand-new ContactCompany should be an explicit click
  //     so the user can read the name they're committing to before it's
  //     written to the CRM.
  // Existing-option selection via Enter still works when not loading.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter') return
      if (isFetching) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      if (selectInstanceRef.current?.state?.focusedOption?.__isNew__) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [isFetching]
  )

  return (
    <CreatableSelect
      className={className}
      style={{ flex: 1, flexShrink: 0, width: '100%' }}
      height={height}
      name={name}
      control={control}
      type="text"
      label={label}
      onSearchChange={setSearchCompany}
      placeholder={placeholder}
      isLoading={isFetching}
      options={companiesOptions}
      disabled={disabled}
      disableCreate={disableCreate}
      initialSearch={searchCompany}
      menuPosition={menuPosition}
      // Clear search when auto selecting first option
      clearSearchOnSelect={autoSelectFirstOption}
      readOnly={readOnly}
      // Disable filtering as we do it from the API
      disableFiltering
      onCreateOption={(option) =>
        Promise.resolve({
          label: option,
          value: {
            id: -1,
            name: option,
            domain: undefined,
          },
        })
      }
      noOptionsMessage={noOptionsMessage}
      onKeyDown={handleKeyDown}
      selectInstanceRef={selectInstanceRef}
    />
  )
}
